import { FC, ReactNode, createContext, useCallback, useContext, useRef, useState } from "react";
import { C } from "../constants";

// The only shared toast surface in the app — mounted once at the app shell (main.tsx), so a toast
// pushed from one view (e.g. "N ads in queue") survives the buyer navigating to launch for a
// different creator, unlike PerformanceView's earlier view-local toast (tied to that view's own
// useState, so it vanished the moment the route unmounted).

// The one toast slot that UPDATES IN PLACE instead of stacking a new entry, and never auto-dismisses
// on a timer — for a status that stays true for a while (e.g. "3 ads processing"), pushToast's
// stack-and-expire model would either spam a new toast every poll tick or expire mid-generation.
const PERSISTENT_ID = "persistent" as const;

type Tone = "success" | "error" | "progress";
type Toast = { id: number | typeof PERSISTENT_ID; message: string; tone: Tone };

type ToastApi = {
  pushToast: (message: string, tone?: Tone) => void;
  /** Upsert the persistent slot, or pass null to clear it (e.g. once nothing is left processing). */
  setPersistentToast: (message: string | null, tone?: Tone) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

const DISMISS_MS = 5000;

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const pushToast = useCallback((message: string, tone: Tone = "success") => {
    const id = ++nextId.current;
    setToasts(t => [...t, { id, message, tone }]);
    // An error is worth the buyer actually reading, not a 5s flash they can miss mid-task — it stays
    // up until they dismiss it themselves (click-to-close, same as every other toast here).
    if (tone !== "error") {
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), DISMISS_MS);
    }
  }, []);

  const setPersistentToast = useCallback((message: string | null, tone: Tone = "progress") => {
    setToasts(t => {
      const rest = t.filter(x => x.id !== PERSISTENT_ID);
      return message == null ? rest : [{ id: PERSISTENT_ID, message, tone }, ...rest];
    });
  }, []);

  return (
    <ToastContext.Provider value={{ pushToast, setPersistentToast }}>
      {children}
      {toasts.length > 0 && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999,
                      display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
          {toasts.map(t => (
            <div
              key={t.id}
              className="toast"
              onClick={() => t.id !== PERSISTENT_ID && setToasts(cur => cur.filter(x => x.id !== t.id))}
              style={{
                position: "static", cursor: t.id === PERSISTENT_ID ? "default" : "pointer",
                // A SOLID red, not the low-opacity errorBg other error surfaces use (a banner sitting
                // on the page background) — a floating toast over arbitrary page content needs to
                // read as red on its own, not half-transparent over whatever's behind it.
                ...(t.tone === "error"
                  ? { background: C.error, borderColor: C.error, color: "#fff" }
                  : {}),
              }}
            >
              {t.tone === "error" ? "⚠" : t.tone === "progress" ? "⏳" : "✓"} {t.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};
